import { FormEvent, KeyboardEvent, useMemo, useState } from 'react';
import { ArrowUp, Square } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

interface ChatComposerProps {
  input: string;
  onInputChange: (nextValue: string) => void;
  onSubmit: () => void;
  onStop: () => void;
  status: 'submitted' | 'streaming' | 'ready' | 'error';
  hasMessages: boolean;
  disabled?: boolean;
  className?: string;
}

const QUICK_ACTIONS = [
  'Summarize recent pages',
  'Compare two saved topics',
  'What did I save about caching?',
];

export function ChatComposer({
  input,
  onInputChange,
  onSubmit,
  onStop,
  status,
  hasMessages,
  disabled = false,
  className,
}: ChatComposerProps) {
  const [isFocused, setIsFocused] = useState(false);
  const canSend = input.trim().length > 0 && !disabled;
  const isStreaming = status === 'streaming' || status === 'submitted';

  const placeholder = useMemo(() => {
    if (hasMessages) return 'Ask a follow-up...';
    return 'Ask about your pages...';
  }, [hasMessages]);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!canSend || isStreaming) return;
    onSubmit();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      if (!canSend || isStreaming) return;
      onSubmit();
    }
  };

  return (
    <form onSubmit={handleSubmit} className={cn('space-y-2', className)}>
      {(isFocused || input.trim().length > 0) && !hasMessages && (
        <div className="flex flex-wrap gap-2">
          {QUICK_ACTIONS.map(action => (
            <button
              key={action}
              type="button"
              className="rounded-full border border-neutral-200 bg-neutral-50 px-2.5 py-1 text-[11px] text-neutral-600 hover:border-neutral-300 hover:bg-neutral-100"
              onClick={() => onInputChange(action)}
            >
              {action}
            </button>
          ))}
        </div>
      )}

      <div className="relative">
        <Input
          type="text"
          value={input}
          onChange={event => onInputChange(event.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          placeholder={placeholder}
          disabled={disabled}
          className="h-12 w-full rounded-sm border-0 bg-neutral-200 pl-4 pr-12 text-base font-light shadow-none text-neutral-800 placeholder:text-neutral-500 focus-visible:ring-0 focus-visible:ring-offset-0"
        />

        {isStreaming ? (
          <Button
            type="button"
            size="icon"
            onClick={onStop}
            className="absolute right-1.5 top-1/2 h-9 w-9 -translate-y-1/2 rounded-full bg-neutral-700 text-white hover:bg-neutral-800"
            disabled={disabled}
          >
            <Square className="h-3.5 w-3.5 fill-current" />
          </Button>
        ) : (
          <Button
            type="submit"
            size="icon"
            className="absolute right-1.5 top-1/2 h-9 w-9 -translate-y-1/2 rounded-full"
            disabled={!canSend}
          >
            <ArrowUp className="h-4 w-4" />
          </Button>
        )}
      </div>
    </form>
  );
}
